import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';

interface Props {
  onClose: () => void;
  initial?: {
    id?: string;
    name?: string;
    type?: string;
    sector?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    dept?: string[];
  };
}

export function AddCustomerModal({ onClose, initial }: Props) {
  const qc = useQueryClient();
  const editing = !!initial?.id;
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState(initial?.type ?? 'Developer');
  const [sector, setSector] = useState(initial?.sector ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [dept, setDept] = useState((initial?.dept ?? []).join(', '));

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        type,
        sector: sector.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        address: address.trim() || null,
        dept: dept.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (editing && initial?.id) return api.patch(`/customers/${initial.id}`, body);
      return api.post('/customers', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success(editing ? 'Customer updated' : 'Customer added', name);
      onClose();
    },
    onError: (e: any) => toast.error('Save failed', e?.response?.data?.message ?? 'Check fields and try again'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="ac-title">{editing ? '✏ Edit Customer' : '+ New Customer'}</div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Company Name</label><input type="text" className="form-input" id="ac-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="form-row">
            <label className="form-label">Type</label>
            <SearchableSelect
              id="ac-type"
              value={type}
              onChange={setType}
              placeholder="— Select type —"
              options={[
                { value: 'Developer', label: 'Developer' },
                { value: 'MainContractor', label: 'Main Contractor' },
                { value: 'LocalAuthority', label: 'Local Authority' },
                { value: 'HousingAssociation', label: 'Housing Association' },
                { value: 'Private', label: 'Private' },
                { value: 'Retailer', label: 'Retailer' },
                { value: 'PublicSector', label: 'Public Sector' },
                { value: 'Other', label: 'Other' },
              ]}
            />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Sector</label><input type="text" className="form-input" id="ac-sector" value={sector ?? ''} onChange={(e) => setSector(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Phone</label><input type="tel" className="form-input" id="ac-phone" value={phone ?? ''} onChange={(e) => setPhone(e.target.value)} /></div>
        </div>
        <div className="form-row"><label className="form-label">Address</label><input type="text" className="form-input" id="ac-address" value={address ?? ''} onChange={(e) => setAddress(e.target.value)} /></div>
        <div className="form-row"><label className="form-label">Email</label><input type="email" className="form-input" id="ac-email" value={email ?? ''} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="form-row"><label className="form-label">Departments (comma separated)</label><input type="text" className="form-input" id="ac-dept" value={dept} onChange={(e) => setDept(e.target.value)} /></div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" id="ac-save-btn" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save Customer'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
